/**
 * CSS Layout value constants for SPDS components.
 *
 * These constants provide a consistent vocabulary for CSS flexbox and grid
 * layout properties across the design system.
 */

// Alignment values (used in align-* and justify-* properties)
export const AUTO = 'auto';
export const BASELINE = 'baseline';
export const CENTER = 'center';
export const END = 'end';
export const FLEX = 'flex';
export const FLEX_END = 'flex-end';
export const FLEX_START = 'flex-start';
export const INHERIT = 'inherit';
export const INITIAL = 'initial';
export const INLINE_FLEX = 'inline-flex';
export const START = 'start';
export const STRETCH = 'stretch';

// Distribution values (used in align-content and justify-content)
export const SPACE_AROUND = 'space-around';
export const SPACE_BETWEEN = 'space-between';
export const SPACE_EVENLY = 'space-evenly';

// Flex wrap values
export const NOWRAP = 'nowrap';
export const WRAP = 'wrap';
export const WRAP_REVERSE = 'wrap-reverse';

// Grid auto-flow values
export const COLUMN = 'column';
export const COLUMN_DENSE = 'column dense';
export const DENSE = 'dense';
export const ROW = 'row';
export const ROW_DENSE = 'row dense';

export const NONE = 'none';

// Pre-composed options arrays for shared arguments
export const ALIGN_CONTENT_OPTIONS = Object.freeze([
  CENTER,
  FLEX_END,
  FLEX_START,
  INHERIT,
  INITIAL,
  SPACE_AROUND,
  SPACE_BETWEEN,
  STRETCH,
]);

export const ALIGN_ITEMS_OPTIONS = Object.freeze([
  BASELINE,
  CENTER,
  FLEX_END,
  FLEX_START,
  INHERIT,
  INITIAL,
  STRETCH,
]);

export const ALIGN_SELF_OPTIONS = Object.freeze([
  AUTO,
  BASELINE,
  CENTER,
  FLEX_END,
  FLEX_START,
  INHERIT,
  INITIAL,
  STRETCH,
]);

export const FLEX_WRAP_OPTIONS = Object.freeze([INHERIT, INITIAL, NOWRAP, WRAP, WRAP_REVERSE]);

export const GRID_AUTO_FLOW_OPTIONS = Object.freeze([
  COLUMN,
  COLUMN_DENSE,
  DENSE,
  INHERIT,
  INITIAL,
  ROW,
  ROW_DENSE,
]);

export const JUSTIFY_CONTENT_OPTIONS = Object.freeze([
  CENTER,
  FLEX_END,
  FLEX_START,
  INHERIT,
  INITIAL,
  SPACE_AROUND,
  SPACE_BETWEEN,
  SPACE_EVENLY,
]);

export const JUSTIFY_ITEMS_OPTIONS = Object.freeze([CENTER, END, INHERIT, INITIAL, START, STRETCH]);

export const JUSTIFY_SELF_OPTIONS = JUSTIFY_ITEMS_OPTIONS; // Identical options

export const LAYOUT_TYPES = Object.freeze([COLUMN, ROW]);

// Validation constraint arrays (for forbids/requires in metadata)
export const FORBIDS_GRID = Object.freeze([
  { name: 'grid', reason: 'Cannot be used with grid layout' },
]);

export const FORBIDS_FLEXBOX = Object.freeze([
  { name: 'column', reason: 'Cannot use column layout with grid layout' },
  { name: 'flexbox', reason: 'Cannot explicitly enable flexbox layout with grid layout' },
  { name: 'reverse', reason: 'Cannot reverse order when using grid layout' },
]);

export const REQUIRES_GRID = Object.freeze([
  { name: 'grid', reason: 'Grid properties require @grid to be set' },
]);

// Fit content values
export const FIT_CONTENT = 'fitContent';
export const FIT_CONTENT_WIDTH = 'fitContentWidth';
export const FIT_CONTENT_HEIGHT = 'fitContentHeight';

export default {
  // Individual values
  AUTO,
  BASELINE,
  CENTER,
  COLUMN,
  COLUMN_DENSE,
  DENSE,
  END,
  FLEX_END,
  FLEX_START,
  INHERIT,
  INITIAL,
  LAYOUT_TYPES,
  NONE,
  NOWRAP,
  ROW,
  ROW_DENSE,
  SPACE_AROUND,
  SPACE_BETWEEN,
  SPACE_EVENLY,
  START,
  STRETCH,
  WRAP,
  WRAP_REVERSE,
  // Pre-composed arrays
  ALIGN_CONTENT_OPTIONS,
  ALIGN_ITEMS_OPTIONS,
  ALIGN_SELF_OPTIONS,
  FLEX_WRAP_OPTIONS,
  GRID_AUTO_FLOW_OPTIONS,
  JUSTIFY_CONTENT_OPTIONS,
  JUSTIFY_ITEMS_OPTIONS,
  JUSTIFY_SELF_OPTIONS,
  // Validation constraints
  FORBIDS_FLEXBOX,
  FORBIDS_GRID,
  REQUIRES_GRID,
  // Fit content
  FIT_CONTENT,
  FIT_CONTENT_WIDTH,
  FIT_CONTENT_HEIGHT,
};
